import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
    FormControlLabel,
    FormGroup,
    Grid,
    Switch,
    TextField,
    Typography,
    Box,
} from '@mui/material';
import { updateNotificationFrequency } from '../../actions/notification';

const HAT_COPY = {
    HOST: {
        label: 'Hosting opportunities',
        explanation: (n) =>
            `When an artist proposes a concert in my area, don’t email me about it if you’ve emailed me about another proposal in the last ${
                n > 1 ? n + ' days' : 'day'
            }. Multiple proposals will be batched and sent together in one email notification.`,
    },
    ATTENDEE: {
        label: 'Events to attend',
        explanation: (n) =>
            `When there’s a nearby or invited event I might attend, don’t email me about it if you’ve emailed me about another one in the last ${
                n > 1 ? n + ' days' : 'day'
            }. Multiple events will be batched and sent together in one email notification.`,
    },
    ARTIST: {
        label: 'Booking offers',
        explanation: (n) =>
            `When a host offers to book a show, don’t email me about it if you’ve emailed me about another offer in the last ${
                n > 1 ? n + ' days' : 'day'
            }. Multiple offers will be batched and sent together in one email notification.`,
    },
};

const normalizeFrequency = (value, fallback = 7) => {
    if (value === 0 || value === '0') return 0;
    const n = Number(value);
    if (!Number.isFinite(n) || n < 1) {
        return fallback;
    }
    return n;
};

const HatFrequencyRow = ({
    hat,
    value,
    disabled,
    hideHatLabel,
    onCommit,
}) => {
    const copy = HAT_COPY[hat] || HAT_COPY.ATTENDEE;
    const [localValue, setLocalValue] = useState(normalizeFrequency(value, 7));
    const lastSaved = useRef(normalizeFrequency(value, 7));

    useEffect(() => {
        const next = normalizeFrequency(value, lastSaved.current || 7);
        setLocalValue(next);
        lastSaved.current = next;
    }, [value]);

    const enabled = localValue !== 0;

    const commit = (next) => {
        const normalized = next < 1 ? 0 : Number(next);
        setLocalValue(normalized);
        if (normalized === lastSaved.current) return;
        lastSaved.current = normalized;
        onCommit(hat, normalized);
    };

    return (
        <FormGroup
            sx={{
                alignItems: hideHatLabel ? 'center' : 'flex-start',
                marginTop: hideHatLabel ? 0 : '12px',
                width: '100%',
            }}
        >
            {!hideHatLabel && (
                <Typography
                    component="h3"
                    sx={{
                        fontSize: '1rem',
                        marginBottom: '4px',
                        width: '100%',
                    }}
                >
                    {copy.label}
                </Typography>
            )}
            <Grid item sx={{ width: '100%' }}>
                <Box
                    sx={{
                        display: 'flex',
                        flexWrap: { xs: 'wrap', sm: 'nowrap' },
                        alignItems: 'center',
                        columnGap: '4px',
                        whiteSpace: { xs: 'normal', sm: 'nowrap' },
                    }}
                >
                    <FormControlLabel
                        control={
                            <Switch
                                checked={enabled}
                                disabled={disabled}
                                onChange={() => commit(enabled ? 0 : 7)}
                            />
                        }
                        label={
                            enabled ? 'Send me emails' : "Don't send me emails"
                        }
                        sx={{ display: 'inline-flex', margin: '0', flexShrink: 0 }}
                    />
                    {enabled && (
                        <>
                            <Typography sx={{ display: 'inline' }}>
                                as often as once every
                            </Typography>
                            <TextField
                                sx={{
                                    width: '40px',
                                    margin: '3px 0 -3px',
                                    textAlign: 'center',
                                    flexShrink: 0,
                                    '& input': {
                                        textAlign: 'center',
                                    },
                                }}
                                variant="standard"
                                name={`notificationFrequency-${hat}`}
                                id={`notificationFrequency-${hat}`}
                                value={localValue}
                                disabled={disabled}
                                onChange={(e) => {
                                    const raw = e.target.value;
                                    if (raw === '' || raw === '-') {
                                        setLocalValue(raw);
                                        return;
                                    }
                                    const n = Number(raw);
                                    setLocalValue(n < 1 ? 0 : n);
                                    if (n < 1) {
                                        commit(0);
                                    }
                                }}
                                onBlur={(e) => {
                                    const n = Number(e.target.value);
                                    commit(n < 1 ? 0 : n);
                                }}
                                type="number"
                                inputProps={{ min: 0 }}
                            />
                            <Typography sx={{ display: 'inline' }}>
                                {localValue > 1 ? 'days.' : 'day.'}
                            </Typography>
                        </>
                    )}
                </Box>
                {enabled && (
                    <Typography
                        component="span"
                        sx={{
                            fontSize: '.8em',
                            display: 'block',
                            color: 'var(--primary-color)',
                            marginTop: '4px',
                            whiteSpace: 'normal',
                        }}
                    >
                        ({copy.explanation(localValue)})
                    </Typography>
                )}
            </Grid>
        </FormGroup>
    );
};

HatFrequencyRow.propTypes = {
    hat: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    disabled: PropTypes.bool,
    hideHatLabel: PropTypes.bool,
    onCommit: PropTypes.func.isRequired,
};

const NotificationFrequencySettings = ({
    auth,
    host,
    attendee,
    artist,
    hats,
    hideHatLabel,
    frequencyValues,
    updateNotificationFrequency,
    onFrequencyChange,
}) => {
    const roles = useMemo(() => {
        const r = auth && auth.user && auth.user.role;
        if (Array.isArray(r)) return r;
        if (typeof r === 'string' && r.trim()) return [r.trim()];
        return [];
    }, [auth]);

    const availableHats = useMemo(() => {
        const wanted = Array.isArray(hats) && hats.length > 0 ? hats : null;
        return ['HOST', 'ARTIST', 'ATTENDEE'].filter((hat) => {
            if (wanted && wanted.indexOf(hat) === -1) return false;
            return roles.indexOf(hat) !== -1 || (wanted && wanted.includes(hat));
        });
    }, [hats, roles]);

    const values = {
        HOST:
            frequencyValues && frequencyValues.HOST !== undefined
                ? frequencyValues.HOST
                : host && host.me && host.me.notificationFrequency !== undefined
                  ? host.me.notificationFrequency
                  : 7,
        ARTIST:
            frequencyValues && frequencyValues.ARTIST !== undefined
                ? frequencyValues.ARTIST
                : artist &&
                    artist.me &&
                    artist.me.notificationFrequency !== undefined
                  ? artist.me.notificationFrequency
                  : 7,
        ATTENDEE:
            frequencyValues && frequencyValues.ATTENDEE !== undefined
                ? frequencyValues.ATTENDEE
                : attendee &&
                    attendee.me &&
                    attendee.me.notificationFrequency !== undefined
                  ? attendee.me.notificationFrequency
                  : 7,
    };

    const handleCommit = (hat, value) => {
        updateNotificationFrequency(hat, value);
        if (onFrequencyChange) {
            onFrequencyChange(hat, value);
        }
    };

    if (availableHats.length === 0) {
        return (
            <Typography sx={{ opacity: 0.7 }}>
                No notification hats to configure yet.
            </Typography>
        );
    }

    return (
        <>
            {availableHats.map((hat) => (
                <HatFrequencyRow
                    key={hat}
                    hat={hat}
                    value={values[hat]}
                    hideHatLabel={!!hideHatLabel}
                    disabled={
                        hat === 'HOST' &&
                        !(host && host.me && host.me.active)
                    }
                    onCommit={handleCommit}
                />
            ))}
        </>
    );
};

NotificationFrequencySettings.propTypes = {
    auth: PropTypes.object.isRequired,
    host: PropTypes.object,
    attendee: PropTypes.object,
    artist: PropTypes.object,
    hats: PropTypes.arrayOf(PropTypes.string),
    hideHatLabel: PropTypes.bool,
    frequencyValues: PropTypes.object,
    updateNotificationFrequency: PropTypes.func.isRequired,
    onFrequencyChange: PropTypes.func,
};

const mapStateToProps = (state) => ({
    auth: state.auth,
    host: state.host,
    attendee: state.attendee,
    artist: state.artist,
});

export default connect(mapStateToProps, { updateNotificationFrequency })(
    NotificationFrequencySettings
);
